// @ts-strict-ignore
import React, { SetStateAction, useContext, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { Button, DropdownMenu, DropdownMenuItem, Icon } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds, WalletType, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { useMappedRoles } from '../../../../hooks/useMappedRoles'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../Pay/atoms/atoms'

interface Props {
  setIsSheetOpen?: React.Dispatch<SetStateAction<boolean>>
  type: WalletTypes
  wallet: WalletType
}

const Submenu = ({ setIsSheetOpen, type, wallet }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const { viewOnly } = useProfileContext()
  const { enableUsLaunchRestrictions } = useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const customerModel = useAtomValue(customerModelAtom)

  const isNetworkWallet = wallet?.supportsInternalBvnkNetworkTransfers
  const canEdit = permissions?.wallets?.canEdit
  const canTrade = permissions?.wallets?.canTrade
  const canEditOrTrade = canEdit || canTrade
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const canPay = isNetworkWallet ? permissions?.bvnkNetwork?.canTrade : canEdit || canTrade

  const isSelectedWalletActive = useIsActiveWallet(wallet)
  const isFiat = type?.toUpperCase() === WalletTypes.FIAT
  const hasBalance = wallet?.balance > 0

  const handleOpenConvertSheet = () => {
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenWalletEditSheet = () => {
    setWalletAction({
      actionId: ActionIds?.WALLET_EDIT,
      header: t('wallets.editWalletName'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = () => {
    setWalletAction({
      actionId: ActionIds.MOVE,
      header: t('customers.moveFunds'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWalletType: WalletTransferType.MAIN,
        sourceWallet: wallet,
        customerModel,
      },
    })
    setIsSheetOpen(true)
  }

  const { supportsThirdParty, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
    wallet

  const isPayEnabled = supportsThirdParty && canPay && isSelectedWalletActive && !viewOnly
  const isWithdrawalEnabled =
    supportsWithdrawals && canEditOrTrade && isSelectedWalletActive && !viewOnly
  const isUnifiedPayoutEnabled = isPayEnabled || isWithdrawalEnabled

  const handleOpenFiatDepositSheet = (walletId: number) => {
    if (walletId) {
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        sourceWalletId: wallet?.id,
      })
    }
    setIsSheetOpen(true)
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleOpenSendFiatSheet = () => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: wallet?.lsid,
      })

      setIsSheetOpen(true)
    }
  }

  const createTransactionHistoryMenuItem = () => (
    <DropdownMenuItem
      key="transaction-history"
      LeadingIcon={<Icon name="ClockCounterClockwise" />}
      onClick={() =>
        navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=recent-activity`)
      }
    >
      {t('wallets.transactionHistory')}
    </DropdownMenuItem>
  )

  const createEditWalletMenuItem = () => {
    if (canEdit && isSelectedWalletActive && !viewOnly) {
      return (
        <DropdownMenuItem
          key="edit-wallet"
          LeadingIcon={<Icon name="SimplePencilCustom" />}
          onClick={() => handleOpenWalletEditSheet()}
        >
          {t('wallets.editWalletName')}
        </DropdownMenuItem>
      )
    }
    return null
  }

  const createWalletDetailsMenuItem = () => {
    const shouldShow = isFiat
      ? supportsDeposits && canEditOrTrade && isSelectedWalletActive
      : canEditOrTrade && isSelectedWalletActive

    if (shouldShow) {
      return (
        <DropdownMenuItem
          key="wallet-details"
          LeadingIcon={<Icon name="Info" />}
          onClick={() => {
            navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=recent-activity`)
          }}
        >
          {t('wallets.walletDetails')}
        </DropdownMenuItem>
      )
    }
    return null
  }

  const createSendMenuItem = () => {
    const shouldShow = isFiat
      ? hasBalance && isSelectedWalletActive && canCreatePayOut && !viewOnly
      : canEditOrTrade && isSelectedWalletActive && hasBalance && canCreatePayOut && !viewOnly

    if (shouldShow) {
      const key = isFiat ? 'send-crypto' : 'send'
      const text = isFiat ? t('payments.sendCrypto') : t('transactionAction.send')

      return (
        <DropdownMenuItem
          key={key}
          LeadingIcon={<Icon name="ArrowUpRight" />}
          onClick={() => {
            setWalletAction({
              actionId: ActionIds?.SEND,
              header: t('wallets.send.contactStepHeader'),
              onClose: () => {
                setIsSheetOpen(false)
              },
              meta: {
                sourceWallet: wallet,
              },
            })
            setIsSheetOpen(true)
          }}
        >
          {text}
        </DropdownMenuItem>
      )
    }
    return null
  }

  const createConvertMenuItem = () => {
    const shouldShow =
      canEditOrTrade && isSelectedWalletActive && !viewOnly && !enableUsLaunchRestrictions

    if (shouldShow) {
      return (
        <DropdownMenuItem
          key="convert"
          LeadingIcon={<Icon name="ArrowsLeftRight" />}
          onClick={() => handleOpenConvertSheet()}
        >
          {t('transactionAction.convert')}
        </DropdownMenuItem>
      )
    }
    return null
  }

  const createFiatSpecificMenuItems = () => {
    const menuItems = []

    if (isUnifiedPayoutEnabled) {
      menuItems.push(
        <DropdownMenuItem
          key="send-fiat"
          LeadingIcon={<Icon name="CreditCard" />}
          onClick={() => {
            handleOpenSendFiatSheet()
          }}
        >
          {t('wallets.fiatSend.sendFiat')}
        </DropdownMenuItem>
      )
    }

    const isDepositButtonEnabled =
      supportsDeposits && canEditOrTrade && isSelectedWalletActive && !viewOnly
    if (isDepositButtonEnabled) {
      menuItems.push(
        <DropdownMenuItem
          key="deposit"
          LeadingIcon={<Icon name="CoinArrowInCustom" />}
          onClick={() => {
            handleOpenFiatDepositSheet(wallet?.id)
          }}
        >
          {t('transactionAction.deposit')}
        </DropdownMenuItem>
      )
    }

    const isMoveFundsButtonEnabled =
      isSelectedWalletActive && supportsInternalTransfers && !viewOnly
    if (isMoveFundsButtonEnabled) {
      menuItems.push(
        <DropdownMenuItem
          key="move"
          LeadingIcon={<Icon name="ArrowsLeftRight" />}
          onClick={handleOpenMoveSheet}
        >
          {t('customers.move')}
        </DropdownMenuItem>
      )
    }

    return menuItems
  }

  const createCryptoSpecificMenuItems = () => {
    const menuItems = []

    // Receive
    if (canEditOrTrade && isSelectedWalletActive) {
      menuItems.push(
        <DropdownMenuItem
          key="receive"
          LeadingIcon={<Icon name="ArrowDownLeft" />}
          onClick={() => {
            navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=receive-crypto`)
          }}
        >
          {t('transactionAction.receive')}
        </DropdownMenuItem>
      )
    }

    if (supportsInternalTransfers) {
      menuItems.push(
        <DropdownMenuItem
          key="move"
          LeadingIcon={<Icon name="ArrowsLeftRight" />}
          onClick={handleOpenMoveSheet}
        >
          {t('customers.move')}
        </DropdownMenuItem>
      )
    }

    return menuItems
  }

  const renderMenuItems = () => {
    const commonItems = [
      createTransactionHistoryMenuItem(),
      createEditWalletMenuItem(),
      createWalletDetailsMenuItem(),
      createSendMenuItem(),
      createConvertMenuItem(),
    ].filter(Boolean)

    const specificItems = isFiat ? createFiatSpecificMenuItems() : createCryptoSpecificMenuItems()

    return [...commonItems, ...specificItems]
  }

  const popOverMenuKey = useMemo(() => {
    if (isFiat) {
      // Ensure popover re-renders if capability values change
      const actionsKeys = [
        ...(supportsThirdParty ? ['p'] : []),
        ...(supportsDeposits ? ['d'] : []),
        ...(supportsWithdrawals ? ['w'] : []),
        ...(supportsInternalTransfers ? ['m'] : []),
      ]?.toString()

      return `fiat-popover-${wallet?.lsid}-${actionsKeys}`
    }

    return `crypto-popover-${wallet?.lsid}`
  }, [isFiat, wallet])

  return (
    <DropdownMenu
      key={popOverMenuKey}
      DropdownMenuItems={renderMenuItems()}
      data-testid={isFiat ? 'fiatContextPopoverMenu' : 'cryptoContextPopoverMenu'}
    >
      <Button variant="ghost" iconElement={<Icon name="DotsThreeVerticalCustom" />} />
    </DropdownMenu>
  )
}

export default Submenu
